<template>
  <div class="theme-light">
    <NoInternet />

    <ErrorModal />

    <InstallModal />

    <div
      class="fixed top-0 z-[49] w-full bg-background"
      :style="{ height: iosPlatformHeight }"
    />

    <div
      class="fixed top-0 z-[49] w-full bg-background"
      :style="{ height: iosPlatformHeight }"
    />

    <div class="mt-2">
      <div
        v-if="appName"
        class="flex justify-center text-sm text-grey text-opacity-30"
      >
        {{ appName }}
      </div>
      <div
        v-if="platformApp === 'Android' || platformApp === 'IOS'"
        class="flex space-x-2 justify-center"
      >
        <div
          v-if="platformApp"
          class="flex justify-center text-sm text-grey text-opacity-30"
        >
          {{ platformApp }}
        </div>
        <div
          v-if="appVersion"
          class="flex justify-center text-sm text-grey text-opacity-30"
        >
          v{{ currentAppVersion }}
        </div>
      </div>
      <div
        class="flex justify-center select-none"
        :class="currentRoutePath == '/notifications' ? 'space-x-2' : ''"
      >
        <div
          class="flex justify-center text-sm text-grey text-opacity-30"
          v-if="userData && userData.phone_number && currentRoutePath != '/'"
        >
          {{
            currentRoutePath != "/notifications"
              ? ""
              : "Выплаты аккаунта: " + userData.phone_number
          }}
        </div>
        <div
          v-if="platformApp === 'Pwa' || platformApp === 'Web'"
          class="flex justify-center text-sm text-grey text-opacity-30"
        >
          {{ platformApp }}
        </div>
      </div>
    </div>
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <keep-alive
          :include="['HomeView', 'LoginPage', 'TelegramBanner', 'ReferralPage']"
        >
          <component :is="Component" />
        </keep-alive>
      </transition>
    </router-view>

    <div v-if="layout" class="navigation-area z-20 bg-greyLight">
      <div
        @click="onClickTab(tab.route)"
        class="tab"
        :style="
          hasBottomPadding ? `padding-bottom: ${navigationBottomPadding}px` : ''
        "
        v-for="tab in navigationTabs"
        :key="tab.icon"
        :class="{ 'tab-active': tab.route === currentRoute }"
      >
        <div
          class="tab-icon text-darkGrey"
          :class="{ 'text-greyLight': tab.route === currentRoute }"
        >
          <i :class="tab.icon" />
        </div>
        <div class="text-[10px] text-grey">
          {{ tab.title }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import ErrorModal from "@/components/Modals/ErrorModal.vue";
import InstallModal from "@/components/Modals/InstallModal.vue";
import NoInternet from "@/components/Statuses/NoInternet.vue";
import store from "@/store";
import { IUserData } from "@/struct/IStore/IStore";
import UtilStorage from "@/util/Common/UtilStorage";
import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { SafeArea } from "capacitor-plugin-safe-area";
import { Action, Getter, Mutation } from "s-vuex-class";
import { Options, Vue } from "vue-class-component";

@Options({
  components: { InstallModal, NoInternet, ErrorModal },
})
export default class App extends Vue {
  @Mutation("setDeferredPrompt") private setDeferredPrompt:
    | ((value: any) => void)
    | undefined;
  @Action("getAppInfo") private getAppInfo: (() => void) | undefined;
  @Getter("appName") private appName: string | undefined;
  @Action("getUserData") private getUserData: (() => void) | undefined;
  @Getter("getUserData") private userData: IUserData | undefined;
  public platform = Capacitor.getPlatform();
  @Getter("validateUserPlatform") private platformApp: string | undefined;
  $router: any;

  private hasBottomPadding = true;

  private isOnline = navigator.onLine;

  mounted() {
    CapacitorApp.addListener("appUrlOpen", async (data) => {
      if (data.url.includes("login-yandex")) {
        const url = new URL(data.url);
        this.$router.push({
          path: "/login-yandex/",
          query: {
            code: url.searchParams.get("code"),
            state: url.searchParams.get("state"),
          },
        });
        try {
          await Browser.close();
        } catch (e) {
          console.error(e);
        }
      }
    });
  }

  get currentRoutePath(): string {
    return this.$route.path;
  }
  public currentAppVersion = store.state.currentAppVersion;
  public navigationBottomPadding = 10;
  private iosPlatformHeight: string | null = null;

  private navigationTabs: { icon: string; route: string; title: string }[] = [
    {
      icon: "fas fa-home",
      route: "/home",
      title: "Главная",
    },
    {
      icon: "fas fa-bell",
      route: "/notifications",
      title: "Уведомления",
    },
    {
      icon: "fas fa-cog",
      route: "/settings",
      title: "Настройки",
    },
  ];

  get layout() {
    return this.$route.meta.layout || false;
  }

  get currentRoute() {
    return this.$route.path;
  }

  private onClickTab(link: string) {
    this.$router.replace(link);
  }

  private handleOnline() {
    this.isOnline = true;
  }

  private handleOffline() {
    this.isOnline = false;
  }

  created() {
    this.getAppInfo?.();

    if (this.$PLATFORM == "web") {
      window.addEventListener("beforeinstallprompt", (event) => {
        // Prevent the default behavior of the event
        event.preventDefault();
        // Save the event for later
        this.setDeferredPrompt?.(event);
        UtilStorage.removeItem("appInstalled");
      });
    }

    if (this.$PLATFORM == "web") {
      this.navigationTabs.splice(2, 0, {
        icon: "fas fa-money-bill-wave",
        route: "/referral-page",
        title: "Бонусы",
      });
    } else {
      this.navigationTabs.splice(2, 0, {
        icon: "fas fa-user",
        route: "/profile",
        title: "Профиль",
      });
    }
    this.initializeSafeArea();

    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);
  }

  beforeUnmount() {
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);
  }
  private async initializeSafeArea() {
    try {
      if (this.$PLATFORM === "ios") {
        const { insets } = await SafeArea.getSafeAreaInsets();
        this.iosPlatformHeight = `${insets.top}px`;
        this.hasBottomPadding = false;
      } else if (this.$IS_PWA && this.$IS_SAFARI) {
        this.hasBottomPadding = false;
      } else {
        this.hasBottomPadding = true;
      }
    } catch (e) {
      console.error("Ошибка при получении safe area insets", e);
      this.hasBottomPadding = true;
    }
  }
}
</script>
